import type { BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import { type ReactNode, useState } from "react";

import { BTText } from "@btnet/core-components";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { FileCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/fileCustomFieldDefinition";
import type { FileCustomFieldValue } from "@btnet/services-btnet/handlers/models/fileCustomFieldValue";

import { BdsIconTrash } from "@buildertrend/components";

import { TempFileTypes } from "types/enum";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTUpload } from "commonComponents/btWrappers/BTUpload/BTUpload";
import {
    confirmFileRemove,
    FileFieldDisplay,
    FileFieldEditor,
    type IFileCustomFieldData,
    isConfirmedFileValue,
} from "commonComponents/entity/customField/utils/customFieldTypes/FileFieldDisplay";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

import { FileCellEditor } from "./FileCellEditor";

export { type IFileCustomFieldData } from "commonComponents/entity/customField/utils/customFieldTypes/FileFieldDisplay";

/**
 * Temporary file upload payload stored as a JSON string in the value field.
 * Must be a string (not an object) because the API uses a single string
 * `value` field for all custom field types.
 */
export interface IPendingFileUpload {
    tempId: number;
    ext: string | null;
    fileName: string | null;
}

interface IFileGridUploadButtonProps {
    customFieldId: number;
    allowedFileTypes: string[];
    onCellValueChange: (newValue: unknown) => void;
}

function FileGridUploadButton({
    customFieldId,
    allowedFileTypes,
    onCellValueChange,
}: IFileGridUploadButtonProps) {
    const [uploading, setUploading] = useState(false);

    const handleUploadComplete = (
        _field: string,
        fileUpload: BTServiceFileUpload | BTServiceFileUpload[] | undefined,
    ) => {
        if (!fileUpload || Array.isArray(fileUpload)) return;
        onCellValueChange(
            JSON.stringify({
                tempId: fileUpload.tempId,
                ext: fileUpload.ext,
                fileName: fileUpload.fileName,
            } satisfies IPendingFileUpload),
        );
    };

    return (
        <div className="custom-field-cell-content">
            <div className="custom-field-file-upload-trigger">
                <BTUpload
                    id={`fileCustomFieldGrid-${customFieldId}`}
                    data-testid="fileCustomFieldGridUpload"
                    tempFileType={TempFileTypes.CustomFieldFiles}
                    allowedFileTypes={allowedFileTypes}
                    setFieldValue={handleUploadComplete}
                    value={undefined}
                    useBdsButtonStyle
                    buttonType="tertiary"
                    uploadButtonText=""
                    showUploadList={false}
                    loading={uploading}
                    onStatusChange={(statuses) => {
                        setUploading(statuses.some((s) => s === "uploading"));
                    }}
                />
            </div>
        </div>
    );
}

export const FileCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    FileCustomFieldValue,
    FileCustomFieldDefinition
> = {
    getValue(field): string | null {
        return field.value || null;
    },

    setValue(field, value): FileCustomFieldValue {
        return { ...field, value: value ?? "" };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): FileCustomFieldValue {
        return {
            dataType: "File",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? "",
        };
    },

    compare(valueA: string | null, valueB: string | null): number {
        const a = valueA ?? "";
        const b = valueB ?? "";
        return a.localeCompare(b, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null, _definition, field): ReactNode {
        if (!value || !isConfirmedFileValue(value)) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }

        return (
            <FileFieldDisplay
                fileData={{
                    documentInstanceId: value,
                    fileName: field?.fileName ?? null,
                    fileExtension: field?.fileExtension ?? null,
                    downloadLink: field?.downloadLink ?? null,
                }}
            />
        );
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        _autoFocus?: boolean,
        definition?: FileCustomFieldDefinition,
        field?: FileCustomFieldValue,
    ): ReactNode {
        const allowedFileTypes = definition?.allowedFileTypes ?? ["*"];
        let fileData: IFileCustomFieldData | null = null;
        if (value && isConfirmedFileValue(value)) {
            fileData = {
                documentInstanceId: value,
                fileName: field?.fileName ?? null,
                fileExtension: field?.fileExtension ?? null,
                downloadLink: field?.downloadLink ?? null,
            };
        } else if (value && !isConfirmedFileValue(value)) {
            try {
                const parsed = JSON.parse(value) as Partial<IPendingFileUpload>;
                if (parsed.tempId) {
                    fileData = {
                        documentInstanceId: value,
                        fileName: parsed.fileName ?? null,
                        fileExtension: parsed.ext ?? null,
                        downloadLink: null,
                        tempId: parsed.tempId,
                    };
                }
            } catch {
                // Not valid JSON — ignore
            }
        }

        const handleChange = (newFileData: IFileCustomFieldData | null) => {
            if (!newFileData) {
                onChange(null);
                return;
            }
            if (newFileData.tempId) {
                onChange(
                    JSON.stringify({
                        tempId: newFileData.tempId,
                        ext: newFileData.fileExtension,
                        fileName: newFileData.fileName,
                    } satisfies IPendingFileUpload),
                );
            } else {
                onChange(newFileData.documentInstanceId);
            }
        };

        return (
            <FileFieldEditor
                value={fileData}
                customFieldId={customFieldId}
                allowedFileTypes={allowedFileTypes}
                onChange={handleChange}
                onBlur={onBlur}
            />
        );
    },

    getAgGridCellEditor() {
        return FileCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        const { displayContent, value, canEdit, definition, onCellValueChange } = context;

        if (value && isConfirmedFileValue(value)) {
            return (
                <div className="custom-field-cell-content custom-field-file-display">
                    {displayContent}
                    {canEdit && (
                        <BTButton
                            data-testid="removeFileGridButton"
                            type="tertiary"
                            className="custom-field-file-remove-icon"
                            onClick={() => {
                                confirmFileRemove(() => {
                                    onCellValueChange(null);
                                });
                            }}
                            aria-label="Remove file"
                            icon={<BdsIconTrash />}
                        />
                    )}
                </div>
            );
        }

        if (canEdit) {
            return (
                <FileGridUploadButton
                    customFieldId={context.field?.customFieldId ?? 0}
                    allowedFileTypes={definition.allowedFileTypes ?? ["*"]}
                    onCellValueChange={onCellValueChange}
                />
            );
        }

        return defaultRenderAgGridCell(context);
    },
};
